import { Box } from '@mui/material';
import {
  useDataProvider,
  useLocalization,
  useSnackbar,
} from '@ui-framework/index';
import {
  FormFields,
  IconLibrary,
  PageSection,
  PermissionButton,
} from '@product-framework/index';
import Form from '@product-framework/components/AdvancedForms/Form';
import { useCallback, useRef } from 'react';
import InsightContact from './components/InsightContact';
import schema from './edit.json';
import validationSchema from './validations';
import {
  fetchEmail,
  fetchPhones,
  savePhones,
  saveEmail,
  getServiceModule,
} from './lib';

const FORM_ID = 'ServiceProviderSetupForm';
const PERMISSION_SP = 'SERVICEPROVIDERNOTIFICATION';

function Setup({ serviceProviderId, onChange = () => undefined }) {
  const { formatMessage } = useLocalization();
  const formRef = useRef();
  const snackbar = useSnackbar();
  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { get, getApiUrl, post } = useDataProvider(provider);

  const commonParams = {
    get,
    getApiUrl,
    post,
    serviceProviderId,
    snackbar,
  };

  const fetchDefaultValues = useCallback(async () => {
    let pageData = { email: '', phones: [] };

    const [responseFetchEmail, responseFetchPhones, responseServiceModule] =
      await Promise.all([
        fetchEmail(commonParams),
        fetchPhones(commonParams),
        getServiceModule(commonParams),
      ]);

    if (responseFetchEmail) {
      if (responseFetchEmail.data.length > 0) {
        pageData = { email: responseFetchEmail.data[0]?.email || '' };
      }
    }

    if (responseFetchPhones && responseFetchPhones.data.length > 0) {
      const contactData = responseFetchPhones?.data?.map(item => {
        const { id, serviceProviderId: serviceProviderIdArg, ...other } = item;
        return other;
      });
      if (contactData?.length > 0) {
        pageData = {
          ...pageData,
          phones: contactData,
          insightPurchased: responseServiceModule?.insightPurchased || false,
        };
      }
    }

    const result = {
      data: pageData,
      errors: null,
      warnings: responseFetchPhones?.warnings || [],
    };

    return result;
  }, [serviceProviderId]);

  const onSubmit = async data => {
    const defaultValues = formRef?.current?.getDefaultValues();
    const emailSaveResult = await saveEmail(commonParams, data, defaultValues);
    const phonesSaveResult = await savePhones(
      commonParams,
      data,
      defaultValues,
    );

    const errors = [...emailSaveResult.errors, ...phonesSaveResult.errors];
    const updatedPageData = {
      ...emailSaveResult.updatedPageData,
      ...phonesSaveResult.updatedPageData,
    };

    if (errors.length === 0) {
      if (
        Array.isArray(phonesSaveResult?.warnings) &&
        phonesSaveResult?.warnings?.length > 0
      ) {
        snackbar.showMessage({
          type: 'warning',
          data: formatMessage({
            id: 'SERVICEPROVIDER_NOTIFICATION_SAVE_WARNING',
            defaultMessage: 'Service Provider Setup saved with warnings',
          }),
        });
      } else {
        snackbar.showMessage(
          formatMessage({
            id: 'SERVICEPROVIDER_NOTIFICATION_SAVE_SUCCESS',
            defaultMessage: 'Service Provider Setup saved successfully',
          }),
        );
      }

      return {
        nextValues: {
          ...updatedPageData,
          insightPurchased: defaultValues?.insightPurchased || false,
        },
        errors: null,
        warnings: phonesSaveResult?.warnings || [],
        callback: () => {
          onChange();
        },
      };
    }
    return {
      nextValues: data,
      warnings: phonesSaveResult?.warnings || [],
      errors,
    };
  };

  return (
    <PageSection
      label={formatMessage({
        id: 'SERVICE_PROVIDER_NOTIFICATIONS_EDIT_HEADING',
        defaultMessage: 'Setup',
      })}>
      <Form
        formId={FORM_ID}
        ref={formRef}
        customComponents={{ ...FormFields, InsightContact }}
        permissionEntities={[PERMISSION_SP]}
        onSubmit={onSubmit}
        schema={schema}
        fetchDefaultValues={fetchDefaultValues}
        validationSchema={validationSchema(formatMessage)}>
        <Box display="flex" justifyContent="center" gap={2} mt={3} mb={1}>
          <PermissionButton
            permissionName="CAN_UPDATE_SP_NOTIFICATION_PREFERENCES"
            variant="contained"
            startIcon={<IconLibrary.Save />}
            color="primary"
            onClick={() => formRef.current?.submit({ formId: FORM_ID })}>
            {formatMessage({
              id: 'SERVICEPROVIDER_EDIT_SAVE_SETUP_BUTTON',
              defaultMessage: 'Save Setup',
            })}
          </PermissionButton>
          <PermissionButton
            permissionName="CAN_UPDATE_SP_NOTIFICATION_PREFERENCES"
            variant="contained"
            color="primary"
            startIcon={<IconLibrary.Reset />}
            onClick={() => formRef.current?.reset()}>
            {formatMessage({
              id: 'SERVICEPROVIDER_EDIT_RESET_BUTTON',
              defaultMessage: 'Reset',
            })}
          </PermissionButton>
        </Box>
      </Form>
    </PageSection>
  );
}
export default Setup;
